import {
  EsrsAssessmentActionsQuery_,
  useEsrsAssessmentMaterialStandardsQuery,
  useEsrsAssessmentTargetsQuery,
  useGetEsrsCategoriesQuery,
  useGetMaterialStandardsForSubsidiariesQuery,
} from 'models';
import { useMemo } from 'react';
import { MaterialMetric } from '../MaterialityAssessment';
import { Action } from '../DisclosureRequirements/Actions/Actions.hooks';
import { mfStandardRefs, orderCategories } from 'containers/Esrs/utils';
import { uniq } from 'lodash';
import { drProgressConditions, getRelevantDR } from '..';
import { useParams } from 'react-router-dom';

export type MaterialStandard = {
  standardRef: string;
  standardName: string;
  description?: string | null;
  isMaterialForGroup: boolean | undefined | null;
  isMaterialForSubsidiary: boolean;
  isMaterialityMatchingSubsidiaries?: boolean;
  materialMetrics?: MaterialMetric[];
  parentMaterialMetrics?: MaterialMetric[];
  parentEsrsAssessmentId?: string;
  drRefs: string[];
  frameworkRef: string;
};

export type EsrsCategoryMap = {
  categoryName: string;
  categoryRef: string;
  materialStandards: MaterialStandard[];
};

export type StandardsOverviewData = {
  categories: EsrsCategoryMap[];
  loading: boolean;
};

export const useGetGroupStandardsOverview = (filter: string, esrsAssessmentId?: string) => {
  const { framework = '' } = useParams();
  const { data, loading: loadingAssessment } = useEsrsAssessmentMaterialStandardsQuery({
    variables: { esrsAssessmentId },
    skip: !esrsAssessmentId,
  });

  const {
    data: subsidiaryMaterialityAssessmentData,
    loading: loadingSubsidiaryMaterialityAssessment,
  } = useGetMaterialStandardsForSubsidiariesQuery({
    variables: { groupAssessmentId: esrsAssessmentId },
    skip: !esrsAssessmentId,
  });

  const { data: esrsCategoriesData, loading: loadingCategories } = useGetEsrsCategoriesQuery({
    variables: { frameworkRef: framework },
    skip: !framework,
  });

  const categories = useMemo(() => {
    return orderCategories(esrsCategoriesData?.EsrsCategory ?? []);
  }, [esrsCategoriesData]);

  const groupMaterialityMap: EsrsCategoryMap[] = useMemo(() => {
    const groupMaterialityAssessments = data?.esrsAssessment?.materialStandards;
    const subsidiaryMaterialityAssessments =
      subsidiaryMaterialityAssessmentData?.EsrsAssessment_by_pk?.subsidiaryAssessments;

    const allSubsidiaryAssessments =
      subsidiaryMaterialityAssessmentData?.EsrsAssessment_by_pk?.allAssessments;

    if (groupMaterialityAssessments && subsidiaryMaterialityAssessments) {
      return categories.map((category) => {
        return {
          categoryName: category.title,
          categoryRef: category.reference,
          materialStandards: category.standards
            ?.filter((standard) => {
              const groupMaterialStandard = groupMaterialityAssessments.find((materialStandard) => {
                return materialStandard.standardRef === standard.reference;
              });
              return (
                groupMaterialStandard?.materialMetrics &&
                groupMaterialStandard?.materialMetrics?.some((mm) => mm.isMaterial)
              );
            })
            .map((standard) => {
              const isMFStandard = mfStandardRefs.includes(standard.reference);
              const groupMaterialStandard = groupMaterialityAssessments.find((materialStandard) => {
                return materialStandard.standardRef === standard.reference;
              });

              const standardApDrs =
                (isMFStandard ? standard.specificMFStandard : standard)?.disclosureRequirementGroups
                  .flatMap((drg) => drg.requirements.flatMap((dr) => dr.reference))
                  ?.filter((s): s is string => !!s) ?? [];

              const relevantDRS = uniq(
                groupMaterialStandard?.materialMetrics
                  ?.filter((mm) => drProgressConditions(mm, standardApDrs, framework))
                  ?.map((m) => getRelevantDR(m.metric, framework)) ?? []
              );
              return {
                standardRef: standard.reference,
                standardName: standard.title,
                description: standard.description,
                isMaterialForGroup: groupMaterialStandard?.isMaterial === true,
                isMaterialForSubsidiary: !!subsidiaryMaterialityAssessments?.find(
                  (subsidiaryAssessment) => {
                    return subsidiaryAssessment.materialStandards.find(
                      (materialStandard) => materialStandard.standardRef === standard.reference
                    )?.isMaterial;
                  }
                ),
                isMaterialityMatchingSubsidiaries: allSubsidiaryAssessments
                  ?.map((sub) =>
                    sub.materialStandards.find((ma) => ma.standardRef === standard.reference)
                  )
                  .every(
                    (subsidiaryAssessment) =>
                      subsidiaryAssessment?.isMaterial ===
                      groupMaterialityAssessments.find((materialStandard) => {
                        return materialStandard.standardRef === standard.reference;
                      })?.isMaterial
                  ),
                drRefs: relevantDRS,
                frameworkRef: groupMaterialStandard?.standard.frameworkRef ?? '',
              };
            })
            .filter(
              (standard) =>
                (standard.isMaterialForGroup == true || standard.isMaterialForSubsidiary == true) ??
                []
            ),
        };
      });
    } else return [];
  }, [data, subsidiaryMaterialityAssessmentData, esrsCategoriesData, filter]);

  const loading = useMemo(() => {
    return loadingAssessment || loadingCategories || loadingSubsidiaryMaterialityAssessment;
  }, [loadingAssessment, loadingCategories, loadingSubsidiaryMaterialityAssessment]);

  const groupStandardsOverviewData: StandardsOverviewData = {
    categories: groupMaterialityMap,
    loading,
  };

  return groupStandardsOverviewData;
};

export const useEsrsTargets = (esrsAssessmentId?: string) => {
  const { data, loading } = useEsrsAssessmentTargetsQuery({
    variables: {
      esrsAssessmentId,
    },
    skip: !esrsAssessmentId,
  });

  const hasParentAssessment = useMemo(
    () => !!data?.esrs_Target[0]?.materialStandard?.esrsAssessment?.parentAssessmentId,
    [data]
  );

  const esrsTargets = useMemo(() => {
    return data?.esrs_Target.map((esrsTarget) => {
      const baseline = esrsTarget.keyResults.find((kr) => kr.reportingUnitId === null);
      return {
        ...esrsTarget,
        category: {
          reference: esrsTarget.disclosureRequirement.group.standard.category.reference,
          title: esrsTarget.disclosureRequirement.group.standard.category.title,
        },
        standard: {
          reference: esrsTarget.disclosureRequirement.group.standard.reference,
          title: esrsTarget.disclosureRequirement.group.standard.title,
        },
        isSubOrParent: !esrsTarget.materialStandard.esrsAssessment.company.isGroupOwner,
        companyName: esrsTarget.materialStandard.esrsAssessment.company.name,
        companyId: esrsTarget.materialStandard.esrsAssessment.company.id,
        baseline: Number(baseline?.baseline),
        baseYear: esrsTarget.keyResults?.[0]?.baseYear,
        actions: esrsTarget.actionTargets.map((action: any) => ({
          title: action.action.title,
          id: action.actionId,
        })),
        numberOfActions: esrsTarget?.actionTargets.length,
        targetId: esrsTarget.id,
        milestones: esrsTarget.milestones.map((ml: any) => ({
          year: ml.year,
          value: ml.milestoneResults[0]?.value,
        })),
        owner: esrsTarget.owner,
        title: esrsTarget.name ?? '',
        materialStandardId: esrsTarget.materialStandard.id,
        parentTargetId: esrsTarget.parentTargetId,
        isRequired: esrsTarget.isRequired,
        isFlagged: baseline?.isFlagged ?? false,
        esrsAssessment: esrsTarget.materialStandard.assessmentId,
        metricRef: esrsTarget.metricRef,
        metricTitle: esrsTarget.metric?.title,
      };
    });
  }, [data]);

  return { esrsTargets, loading, hasParentAssessment };
};

export const useEsrsActions = (actionsData?: EsrsAssessmentActionsQuery_) => {
  const actions = useMemo(() => {
    const mapTargets = (action: Action) => {
      return action.actionTargets.map((target: any) => ({
        title: target.target.name ?? '',
        id: target.target.id,
      }));
    };
    return actionsData?.esrs_Actions.map((esrsAction) => {
      return {
        ...esrsAction,
        category: {
          reference: esrsAction.disclosureRequirement.group.standard.category.reference,
          title: esrsAction.disclosureRequirement.group.standard.category.title,
        },
        standardRef: esrsAction.disclosureRequirement.group.standard.reference,
        isSubOrParent: !esrsAction.materialStandard.esrsAssessment.company.isGroupOwner,
        targets: mapTargets(esrsAction),
        numberOfTargets: esrsAction.actionTargets.length,
        companyName: esrsAction.materialStandard.esrsAssessment.company.name,
        actionId: esrsAction.id,
        owner: esrsAction.owner,
        action: esrsAction,
      };
    });
  }, [actionsData]);
  return actions;
};
